import { useRef, type KeyboardEvent } from 'react'
import { clsx } from 'clsx'
import { Calendar } from 'lucide-react'
import { formatDueDate } from '@/lib/dateUtils'

interface DatePickerProps {
  value?: string
  onChange: (date: string) => void
  placeholder?: string
  className?: string
}

export function DatePicker({ value, onChange, placeholder = '날짜 선택', className }: DatePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    try {
      inputRef.current?.showPicker?.()
    } catch {
      // iOS 15 이하 등 showPicker 미지원 시 fallback
      inputRef.current?.click()
    }
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <div
      className={clsx('relative inline-flex items-center gap-2 cursor-pointer focus-within:ring-2 focus-within:ring-primary-500 focus-within:rounded-lg', className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="group"
      aria-label="날짜 선택"
    >
      <Calendar className="w-4 h-4 text-disabled flex-shrink-0" aria-hidden="true" />
      <span className="text-sm text-heading">
        {value ? formatDueDate(value) : placeholder}
      </span>
      <input
        ref={inputRef}
        type="date"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label="날짜 선택"
      />
    </div>
  )
}
